import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Star, Coffee, Smartphone, Shield, Upload } from 'lucide-react';

const CreatorPricing = () => {
  const features = [
    "Unlimited video uploads",
    "Private family sharing", 
    "Mobile app access (iOS)",
    "HD video streaming",
    "Download your videos",
    "Privacy controls",
    "Organized timeline",
    "Email notifications",
    "Cancel anytime"
  ];

  const comparisons = [
    {
      item: "Monthly Coffee Budget",
      price: "$15",
      description: "4-5 coffee shop visits"
    },
    {
      item: "Netflix Subscription",
      price: "$17",
      description: "One streaming service"
    },
    {
      item: "Phone Storage Upgrade",
      price: "$25",
      description: "50GB cloud storage"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <Badge variant="secondary" className="bg-purple-100 text-purple-700 mb-4">
            <Star className="h-4 w-4 mr-2 fill-current" />
            Simple, Honest Pricing
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Less Than Your Coffee Budget
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            For the price of a few coffee shop visits, keep your family connected and your memories safe forever.
          </p>
        </motion.div>

        {/* Price Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-2xl mx-auto mb-16"
        >
          <div className="grid md:grid-cols-3 gap-6">
            {comparisons.map((item, index) => (
              <Card key={index} className="text-center border-gray-200">
                <CardContent className="p-6">
                  <div className="text-2xl font-bold text-gray-400 mb-2">{item.price}</div>
                  <div className="font-semibold text-gray-700 mb-1">{item.item}</div>
                  <div className="text-sm text-gray-500">{item.description}</div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg border-2 border-purple-200">
              <Coffee className="h-5 w-5 text-purple-600 mr-3" />
              <span className="text-lg font-semibold text-gray-800">
                HiveShare Creator: Just <span className="text-purple-600">$9/month</span>
              </span>
            </div>
          </div>
        </motion.div>

        {/* Main Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="max-w-lg mx-auto mb-16"
        >
          <Card className="border-4 border-purple-300 shadow-2xl relative overflow-hidden">
            {/* Popular Badge */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 text-sm font-bold">
                <Star className="h-4 w-4 mr-2 fill-current" />
                Perfect for Families
              </Badge>
            </div>

            <CardHeader className="text-center pt-8 pb-4 bg-gradient-to-br from-purple-50 to-pink-50">
              <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                HiveShare Creator
              </CardTitle>
              <div className="mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-5xl font-bold text-purple-600">$9</span>
                  <div className="text-left">
                    <div className="text-gray-600">/month</div>
                    <div className="text-sm text-gray-500">per family</div>
                  </div>
                </div>
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full inline-block text-sm font-semibold">
                  Start Free • No Credit Card Required
                </div>
              </div>
              <p className="text-gray-600">
                Everything you need to share your family's journey safely
              </p>
            </CardHeader>

            <CardContent className="px-8 pb-8">
              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-start"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <Check className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <Button 
                size="lg" 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-4 text-lg font-semibold mb-4"
              >
                <Upload className="h-5 w-5 mr-2" />
                Start Creating - Free Trial
              </Button>

              <div className="text-center text-sm text-gray-500 space-y-1">
                <p>✅ 14-day free trial • Cancel anytime</p>
                <p>🔒 Your videos stay private</p>
                <p>📱 Works on iPhone and web</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Value Proposition */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <Smartphone className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Mobile First</h3>
              <p className="text-gray-600 text-sm">Upload directly from your phone. No complicated setup or file transfers.</p>
            </div>
            <div>
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Family Safe</h3>
              <p className="text-gray-600 text-sm">Private by default. No ads, no strangers, no algorithms.</p>
            </div>
            <div>
              <Star className="h-12 w-12 text-yellow-500 mx-auto mb-4 fill-current" />
              <h3 className="font-bold text-gray-900 mb-2">Worth Every Penny</h3>
              <p className="text-gray-600 text-sm">Priceless memories, organized and safe, for less than lunch out.</p>
            </div>
          </div>
        </motion.div>

        {/* Growing Your Impact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 max-w-2xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                🚀 Growing Your Impact?
              </h3>
              <div className="space-y-2 text-left max-w-md mx-auto">
                <p className="flex items-center text-gray-700">
                  <Check className="h-4 w-4 text-green-600 mr-2" />
                  Started with family videos
                </p>
                <p className="flex items-center text-gray-700">
                  <Check className="h-4 w-4 text-green-600 mr-2" />
                  Other parents asking for copies
                </p>
                <p className="flex items-center text-gray-700">
                  <Check className="h-4 w-4 text-green-600 mr-2" />
                  Teaching a few kids privately
                </p>
              </div>
              <div className="mt-6 p-4 bg-white rounded-lg border border-blue-200">
                <p className="text-sm text-gray-600 mb-3">
                  ➡️ Ready to manage multiple families? 
                </p>
                <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                  Learn About HiveShare Studio
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorPricing;